import React, { useRef, useState, useEffect, useCallback } from 'react';
import classes from './Register.module.css';
import fl_logo from '../../assets/fl_logo.png';
import { PersonOutlined, LockOpenOutlined } from '@material-ui/icons';
import CustomTextField from '../../ui/CustomTextField';
import CustomPasswordField from '../../ui/CustomPasswordField';
import CustomButton from '../../ui/CustomButton';
import CustomSpinner from '../../ui/CustomSpinner';
import useV3Axios from '../../hooks/useV3Axios';
import { useHistory, useLocation } from 'react-router-dom';
import { LOGIN_PATH } from '../../utils/paths';

const PASSWORD_RULES = [
    { label: 'At least 8 characters', test: v => v.length >= 8 },
    { label: 'One uppercase letter', test: v => /[A-Z]/.test(v) },
    { label: 'One lowercase letter', test: v => /[a-z]/.test(v) },
    { label: 'One digit', test: v => /\d/.test(v) },
    { label: 'One special character', test: v => /[\W_]/.test(v) },
];

const RESEND_COOLDOWN_SECONDS = 60;

const decodeInviteToken = (token) => {
    try {
        return JSON.parse(atob(token.split('.')[1]));
    } catch {
        return null;
    }
};

const RegisterInvite = () => {
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const inviteToken = params.get('token');
    const decoded = inviteToken ? decodeInviteToken(inviteToken) : null;
    const email = decoded?.sub || null;

    const otpRef = useRef(null);
    const usernameRef = useRef(null);
    const passwordRef = useRef(null);
    const confirmRef = useRef(null);

    const [step, setStep] = useState(1); // 1=send OTP, 2=verify OTP, 3=credentials
    const [emailVerifiedToken, setEmailVerifiedToken] = useState(null);
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [countdown, setCountdown] = useState(0);

    const { isLoading: isSending, error: sendError, data: sendData, sendRequest: sendOtp } = useV3Axios();
    const { isLoading: isVerifying, error: verifyError, data: verifyData, sendRequest: verifyOtp } = useV3Axios();
    const { isLoading: isCompleting, error: completeError, data: completeData, sendRequest: completeInvite } = useV3Axios();

    // Invalid token — redirect to login
    useEffect(() => {
        if (!inviteToken || !email) history.replace(LOGIN_PATH);
    }, [inviteToken, email, history]);

    // Countdown timer
    useEffect(() => {
        if (countdown <= 0) return;
        const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [countdown]);

    // Send OTP success → go to step 2
    useEffect(() => {
        if (sendData) {
            setStep(2);
            setCountdown(RESEND_COOLDOWN_SECONDS);
            setError(null);
        }
    }, [sendData]);

    useEffect(() => {
        if (sendError) setError(sendError);
    }, [sendError]);

    // Verify OTP success → go to step 3
    useEffect(() => {
        if (verifyData) {
            setEmailVerifiedToken(verifyData.emailVerifiedToken);
            setStep(3);
            setError(null);
        }
    }, [verifyData]);

    useEffect(() => {
        if (verifyError) setError(verifyError);
    }, [verifyError]);

    // Complete invite success → redirect to login
    useEffect(() => {
        if (completeData) history.push(LOGIN_PATH);
    }, [completeData, history]);

    useEffect(() => {
        if (completeError) setError(completeError);
    }, [completeError]);

    const handleSendOtp = useCallback((event) => {
        event.preventDefault();
        setError(null);
        sendOtp('/register/send-otp', { email });
    }, [sendOtp, email]);

    const handleVerify = useCallback((event) => {
        event.preventDefault();
        setError(null);
        verifyOtp('/register/verify-otp', { email, otp: otpRef.current.value.trim() });
    }, [verifyOtp, email]);

    const handleResend = useCallback(() => {
        if (countdown > 0) return;
        setError(null);
        sendOtp('/register/send-otp', { email });
    }, [sendOtp, email, countdown]);

    const handleComplete = useCallback((event) => {
        event.preventDefault();
        setError(null);

        const pwd = passwordRef.current.value;
        const allMet = PASSWORD_RULES.every(r => r.test(pwd));
        if (!allMet) {
            setError('Password does not meet all requirements.');
            return;
        }
        if (pwd !== confirmRef.current.value) {
            setError('Passwords do not match.');
            return;
        }

        completeInvite('/register/complete-invite', {
            inviteToken,
            emailVerifiedToken,
            username: usernameRef.current.value.trim(),
            password: pwd,
        });
    }, [completeInvite, inviteToken, emailVerifiedToken]);

    if (!inviteToken || !email) return null;

    return (
        <div className={classes.Login}>
            <div className={classes.Card}>
                <img className={classes.Logo} src={fl_logo} alt="logo" />
                <p className={classes.Title}>FetalLite Dashboard</p>

                {/* Step 1: Send OTP */}
                {step === 1 && (
                    <form className={classes.Form} onSubmit={handleSendOtp}>
                        <h3 style={{ textAlign: 'left', margin: '0 0 8px 0', color: '#333' }}>You've been invited</h3>
                        <p style={{ textAlign: 'left', fontSize: '14px', color: '#555', margin: '0 0 16px 0' }}>
                            You were invited as <strong>{email}</strong>. Verify your email to continue.
                        </p>
                        {error && <p className={classes.Error}>{error}</p>}
                        <div className={classes.Container}>
                            {isSending ? <CustomSpinner /> : <CustomButton type='submit' size='large'>SEND OTP</CustomButton>}
                        </div>
                        <hr className={classes.Divider} />
                        <p style={{ textAlign: 'center', fontSize: '14px', color: '#555' }}>
                            <span className={classes.Link} onClick={() => history.push(LOGIN_PATH)}>Back to sign in</span>
                        </p>
                    </form>
                )}

                {/* Step 2: Verify OTP */}
                {step === 2 && (
                    <form className={classes.Form} onSubmit={handleVerify}>
                        <h3 style={{ textAlign: 'left', margin: '0 0 8px 0', color: '#333' }}>Verify your email</h3>
                        <p style={{ textAlign: 'left', fontSize: '14px', color: '#555', margin: '0 0 16px 0' }}>
                            Enter the 6-digit code sent to <strong>{email}</strong>
                        </p>
                        <input
                            ref={otpRef}
                            className={classes.OtpInput}
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            placeholder="------"
                            autoFocus
                            required
                        />
                        <div className={classes.ResendRow}>
                            <span>Didn't receive it?</span>
                            {countdown > 0
                                ? <span style={{ color: '#999' }}>Resend in {countdown}s</span>
                                : <span
                                    className={classes.Link}
                                    onClick={handleResend}
                                    style={{ opacity: isSending ? 0.5 : 1 }}
                                >
                                    {isSending ? 'Sending...' : 'Resend OTP'}
                                </span>
                            }
                        </div>
                        {error && <p className={classes.Error}>{error}</p>}
                        <div className={classes.Container}>
                            {isVerifying ? <CustomSpinner /> : <CustomButton type='submit' size='large'>VERIFY</CustomButton>}
                        </div>
                    </form>
                )}

                {/* Step 3: Set credentials */}
                {step === 3 && (
                    <form className={classes.Form} onSubmit={handleComplete}>
                        <h3 style={{ textAlign: 'left', margin: '0 0 16px 0', color: '#333' }}>Set up your account</h3>
                        <CustomTextField margin="10px auto" reference={usernameRef} required id='username' label='Username' type='text'>
                            <PersonOutlined />
                        </CustomTextField>
                        <div style={{ margin: '10px 0 0 0' }}>
                            <CustomPasswordField reference={passwordRef} id='password' label='Password' onChange={e => setPassword(e.target.value)}>
                                <LockOpenOutlined />
                            </CustomPasswordField>
                            <div className={classes.Hint}>
                                {PASSWORD_RULES.map(rule => (
                                    <div
                                        key={rule.label}
                                        className={`${classes.HintItem} ${rule.test(password) ? classes.HintItemMet : classes.HintItemUnmet}`}
                                    >
                                        <span>{rule.test(password) ? '✓' : '○'}</span>
                                        <span>{rule.label}</span>
                                    </div>
                                ))}
                            </div>
                            <div style={{ margin: '10px 0 0 0' }}>
                                <CustomPasswordField reference={confirmRef} id='confirmPassword' label='Confirm password'>
                                    <LockOpenOutlined />
                                </CustomPasswordField>
                            </div>
                        </div>
                        {error && <p className={classes.Error}>{error}</p>}
                        <div className={classes.Container}>
                            {isCompleting ? <CustomSpinner /> : <CustomButton type='submit' size='large'>COMPLETE REGISTRATION</CustomButton>}
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default RegisterInvite;
